import { checkCompatibility, getCompatibleComponents } from '../data/compatibility';

export { checkCompatibility, getCompatibleComponents };

// Enhanced validation with AI suggestions
export const validateSolution = (selectedComponents, allComponents) => {
  const issues = [];
  const warnings = [];
  const suggestions = [];
  
  // Check if essential layers are covered
  const layersCovered = new Set();
  const phasesCovered = new Set();
  const subLayersCovered = new Set();
  
  selectedComponents.forEach(comp => {
    if (comp.layerId) layersCovered.add(comp.layerId);
    if (comp.phaseId) phasesCovered.add(comp.phaseId);
    if (comp.subLayerId) subLayersCovered.add(comp.subLayerId);
  });
  
  // Essential layers check
  const essentialLayers = {
    'layer1': 'Physical Substrate & Compute',
    'layer3': 'Data Acquisition & Generation',
    'layer4': 'Data Structuring & Storage',
    'layer6': 'Model Architecture & Learning Paradigm',
    'layer8': 'Compilation, Packaging & Inference Engine',
    'layer10': 'Application Logic & User Interface',
    'layer11': 'Observability, Evaluation & Security'
  };
  
  Object.entries(essentialLayers).forEach(([layerId, layerName]) => {
    if (!layersCovered.has(layerId)) {
      warnings.push(`Missing components from ${layerName} layer`);
      suggestions.push({
        type: 'missing-layer',
        layerId,
        layerName,
        message: `Consider adding components from the ${layerName} layer to complete your solution.`,
        priority: 'High',
        recommendedComponents: getRecommendedComponentsForLayer(layerId, allComponents, selectedComponents)
      });
    }
  });
  
  // Check for incompatibilities
  for (let i = 0; i < selectedComponents.length; i++) {
    for (let j = i + 1; j < selectedComponents.length; j++) {
      if (!checkCompatibility(selectedComponents[i], selectedComponents[j])) {
        issues.push(`Incompatibility: ${selectedComponents[i].name} ↔ ${selectedComponents[j].name}`);
        suggestions.push({
          type: 'incompatibility',
          comp1: selectedComponents[i],
          comp2: selectedComponents[j],
          message: `${selectedComponents[i].name} and ${selectedComponents[j].name} may have compatibility issues. Consider alternatives.`,
          priority: 'High',
          alternatives: findAlternatives(selectedComponents[i], selectedComponents[j], allComponents)
        });
      }
    }
  }
  
  // Check for missing security components
  if (!hasTag(selectedComponents, 'security')) {
    warnings.push('No security components selected');
    suggestions.push({
      type: 'security',
      message: 'Add security components like encryption, WAF, or rate limiting to protect your AI solution.',
      priority: 'High'
    });
  }
  
  // Check for missing monitoring
  if (!hasTag(selectedComponents, 'monitoring') && !hasTag(selectedComponents, 'observability')) {
    warnings.push('No monitoring or observability components selected');
    suggestions.push({
      type: 'monitoring',
      message: 'Add monitoring tools like Prometheus, Grafana, or Datadog for production readiness.',
      priority: 'Medium'
    });
  }
  
  // Check for cost optimization
  const highCostComponents = selectedComponents.filter(c => (c.cost?.high || 0) > 10000);
  if (highCostComponents.length > 3) {
    suggestions.push({
      type: 'cost',
      message: `You have ${highCostComponents.length} high-cost components. Consider using managed services or edge deployment to reduce costs.`,
      priority: 'Medium'
    });
  }
  
  // Check for GPU without proper cooling
  const hasGPU = hasTag(selectedComponents, 'gpu');
  const hasCooling = hasTag(selectedComponents, 'cooling') || hasTag(selectedComponents, 'liquid-cooling');
  if (hasGPU && !hasCooling) {
    warnings.push('GPU components selected without proper cooling');
    suggestions.push({
      type: 'cooling',
      message: 'Add liquid cooling or air cooling components for GPU thermal management.',
      priority: 'High'
    });
  }
  
  // Check for LLM without vector database
  const hasLLM = hasTag(selectedComponents, 'llm');
  const hasVectorDB = hasTag(selectedComponents, 'vector');
  if (hasLLM && !hasVectorDB) {
    suggestions.push({
      type: 'rag',
      message: 'Consider adding a vector database (Pinecone, Weaviate, pgvector) for RAG capabilities with your LLM.',
      priority: 'Medium'
    });
  }
  
  return { 
    issues, 
    warnings, 
    suggestions,
    isValid: issues.length === 0 
  };
};

// Helper functions
const hasTag = (components, tag) => {
  return components.some(c => c.tags?.includes(tag));
};

const getRecommendedComponentsForLayer = (layerId, allComponents, selectedComponents) => {
  const selectedIds = new Set(selectedComponents.map(c => c.id));
  return allComponents
    .filter(c => c.layerId === layerId && !selectedIds.has(c.id))
    .slice(0, 5)
    .map(c => ({
      id: c.id,
      name: c.name,
      icon: c.icon,
      purpose: c.purpose,
      cost: c.cost
    }));
};

const findAlternatives = (comp1, comp2, allComponents) => {
  // Find components that are compatible with both
  const alternatives = allComponents.filter(c => 
    c.id !== comp1.id && 
    c.id !== comp2.id &&
    checkCompatibility(c, comp1) && 
    checkCompatibility(c, comp2)
  ).slice(0, 3);
  
  return alternatives.map(c => ({
    id: c.id,
    name: c.name,
    icon: c.icon,
    purpose: c.purpose
  }));
};

// Updated cost calculation with quantity
export const calculateSolutionCost = (selectedComponents) => {
  const totalCost = {
    low: 0,
    high: 0,
    currency: 'USD',
    breakdown: []
  };
  
  selectedComponents.forEach(comp => {
    const quantity = comp.quantity || 1;
    if (comp.cost && comp.cost.low !== undefined) {
      const itemLow = comp.cost.low * quantity;
      const itemHigh = comp.cost.high * quantity;
      
      totalCost.low += itemLow;
      totalCost.high += itemHigh;
      totalCost.breakdown.push({
        componentId: comp.id,
        component: comp.name,
        icon: comp.icon,
        quantity: quantity,
        unitPriceLow: comp.cost.low,
        unitPriceHigh: comp.cost.high,
        low: itemLow,
        high: itemHigh,
        unit: comp.cost.unit
      });
    }
  });
  
  return totalCost;
};